import { useEffect, useMemo, useRef, useState } from "react";
import type { CannedScenario, SubAgentKey } from "../data/types";

interface Props {
  scenario: CannedScenario;
  onComplete: () => void;
}

type CoordinatorPhase = "analyzing" | "dispatching" | "running" | "synthesizing" | "done";
type AgentPhase = "queued" | "running" | "done" | "skipped";

type CoordStep =
  | { kind: "incoming"; incidentId: string; source: string }
  | { kind: "extracted"; fields: string[] }
  | { kind: "dispatch"; agent: SubAgentKey; reason: string }
  | { kind: "skip"; agent: SubAgentKey; reason: string };

interface AgentState {
  phase: AgentPhase;
  output: string;
  progress: number;
  reason: string;
}

// Baseball-card metadata per sub-agent. Tagline + focus + icon glyph drive the
// card visual.
const AGENT_META: Record<
  SubAgentKey,
  { name: string; tagline: string; focus: string; glyph: string; accent: string }
> = {
  command: {
    name: "Command-Line Analyzer",
    tagline: "Decodes commands · identifies LOLBin abuse",
    focus: "Process execution",
    glyph: "</>",
    accent: "#3E74FF", // Blueberry
  },
  identity: {
    name: "Identity Analyzer",
    tagline: "Scores compromise likelihood from auth signals",
    focus: "Who & access",
    glyph: "ID",
    accent: "#941FBA", // Purple
  },
  network: {
    name: "Network Analyzer",
    tagline: "Scores destination reputation + infrastructure",
    focus: "Where & how",
    glyph: "NET",
    accent: "#F6821F", // Tangerine
  },
  activity: {
    name: "Activity Analyzer",
    tagline: "Surfaces temporal patterns + cross-incident correlations",
    focus: "Lead-up context",
    glyph: "ACT",
    accent: "#FBAD41", // Mango
  },
};

// What "field" of the incident each sub-agent is looking at — shown on the card.
function inputPreview(s: CannedScenario, key: SubAgentKey): { label: string; value: string }[] {
  const f = s.incident.extracted_fields;
  switch (key) {
    case "command":
      return [
        { label: "command_line",   value: f.command_line ?? "(none)" },
        { label: "parent_process", value: f.parent_process ?? "(none)" },
      ];
    case "identity":
      return [
        { label: "user_email", value: f.user_email ?? "(none)" },
        { label: "source_ip",  value: f.source_ip ?? "(none)" },
      ];
    case "network":
      return [
        { label: "destination", value: f.destination_domain ?? f.destination_ip ?? "(none)" },
        { label: "process",     value: f.process ?? "(none)" },
      ];
    case "activity":
      return [
        { label: "user_email", value: f.user_email ?? "(none)" },
        { label: "hostname",   value: f.hostname ?? "(none)" },
      ];
  }
}

function verdictLine(s: CannedScenario, key: SubAgentKey): string {
  switch (key) {
    case "command":
      return s.command_findings
        ? `Intent: ${s.command_findings.intent.replace(/_/g, " ")}`
        : "—";
    case "identity":
      return s.identity_findings
        ? `${s.identity_findings.compromise_likelihood} compromise likelihood`
        : "—";
    case "network":
      return s.network_findings
        ? s.network_findings.destination_reputation.replace(/_/g, " ")
        : "—";
    case "activity":
      return s.activity_findings
        ? `${s.activity_findings.baseline_deviations.length} baseline deviations`
        : "—";
  }
}

// Build the per-sub-agent text body the panel "streams" once it finishes.
function buildOutput(s: CannedScenario, key: SubAgentKey): string {
  switch (key) {
    case "command":
      if (!s.command_findings) return "";
      return [
        s.command_findings.decoded_commands[0] ?? "",
        s.command_findings.lolbin_abuse.length
          ? `LOLBin: ${s.command_findings.lolbin_abuse.join(", ")}`
          : "",
        s.command_findings.suspicious_flags.length
          ? `Flags: ${s.command_findings.suspicious_flags.join(", ")}`
          : "",
      ]
        .filter(Boolean)
        .join("\n\n");

    case "identity":
      if (!s.identity_findings) return "";
      return [
        `Anomaly score: ${s.identity_findings.anomaly_score.toFixed(2)}`,
        s.identity_findings.anomaly_signals.length
          ? "Signals:\n" + s.identity_findings.anomaly_signals.map((x) => `  • ${x}`).join("\n")
          : "",
        s.identity_findings.rationale,
      ]
        .filter(Boolean)
        .join("\n\n");

    case "network":
      if (!s.network_findings) return "";
      return [
        `Verdict: ${s.network_findings.verdict}`,
        s.network_findings.infrastructure_signals.asn
          ? `ASN: ${s.network_findings.infrastructure_signals.asn} (${s.network_findings.infrastructure_signals.asn_reputation ?? "?"})`
          : "",
        s.network_findings.infrastructure_signals.hosting_age_days != null
          ? `Hosting age: ${s.network_findings.infrastructure_signals.hosting_age_days}d`
          : "",
        s.network_findings.reputation_sources.length
          ? `Sources: ${s.network_findings.reputation_sources.join(", ")}`
          : "",
      ]
        .filter(Boolean)
        .join("\n");

    case "activity":
      if (!s.activity_findings) return "";
      return [
        `Lookback: ${s.activity_findings.lookback_window_hours}h`,
        s.activity_findings.activity_timeline.length
          ? `Last events:\n${s.activity_findings.activity_timeline
              .slice(-3)
              .map((e) => `  ${e.timestamp.slice(11, 16)} — ${e.event_type}`)
              .join("\n")}`
          : "",
        s.activity_findings.behavioral_assessment,
      ]
        .filter(Boolean)
        .join("\n\n");
  }
}

function streamDuration(textLen: number): number {
  return Math.min(5200, Math.max(2200, textLen * 12));
}

export function TriageInFlight({ scenario, onComplete }: Props) {
  const targets = useMemo(
    () => ({
      command: buildOutput(scenario, "command"),
      identity: buildOutput(scenario, "identity"),
      network: buildOutput(scenario, "network"),
      activity: buildOutput(scenario, "activity"),
    }),
    [scenario],
  );

  const dispatchMap = useMemo(() => {
    const m = new Map<SubAgentKey, { invoke: boolean; reason: string }>();
    for (const d of scenario.dispatch_plan) m.set(d.key, d);
    return m;
  }, [scenario]);

  const [coordPhase, setCoordPhase] = useState<CoordinatorPhase>("analyzing");
  const [coordSteps, setCoordSteps] = useState<CoordStep[]>([]);

  const [agents, setAgents] = useState<Record<SubAgentKey, AgentState>>(() => {
    const initial: Record<SubAgentKey, AgentState> = {
      command:  { phase: "queued", output: "", progress: 0, reason: "" },
      identity: { phase: "queued", output: "", progress: 0, reason: "" },
      network:  { phase: "queued", output: "", progress: 0, reason: "" },
      activity: { phase: "queued", output: "", progress: 0, reason: "" },
    };
    for (const d of scenario.dispatch_plan) {
      initial[d.key].reason = d.reason;
    }
    return initial;
  });

  const startedRef = useRef(false);
  const synthStartedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const intervals: ReturnType<typeof setInterval>[] = [];

    // Phase 1: Coordinator analyzes the incident
    const f = scenario.incident.extracted_fields;
    const present = [
      f.command_line ? "command_line" : null,
      f.user_email ? "user_email" : null,
      f.destination_ip ? "destination_ip" : null,
      f.destination_domain ? "destination_domain" : null,
      f.hostname ? "hostname" : null,
    ].filter(Boolean) as string[];

    const analysisSteps: CoordStep[] = [
      {
        kind: "incoming",
        incidentId: scenario.incident.incident_id,
        source: scenario.incident.source.toUpperCase(),
      },
      { kind: "extracted", fields: present.length ? present : ["(minimal)"] },
      ...scenario.dispatch_plan.map<CoordStep>((d) =>
        d.invoke
          ? { kind: "dispatch", agent: d.key, reason: d.reason }
          : { kind: "skip",     agent: d.key, reason: d.reason },
      ),
    ];

    let stepDelay = 220;
    for (const step of analysisSteps) {
      timers.push(
        setTimeout(() => {
          setCoordSteps((prev) => [...prev, step]);
        }, stepDelay),
      );
      stepDelay += 260;
    }

    // Phase 2: dispatching — mark non-invoked as skipped
    timers.push(
      setTimeout(() => {
        setCoordPhase("dispatching");
        setAgents((prev) => {
          const next = { ...prev };
          for (const d of scenario.dispatch_plan) {
            next[d.key] = {
              ...next[d.key],
              phase: d.invoke ? "queued" : "skipped",
            };
          }
          return next;
        });
      }, stepDelay + 80),
    );

    // Phase 3: Stream invoked sub-agents in parallel
    timers.push(
      setTimeout(() => {
        setCoordPhase("running");
        for (const d of scenario.dispatch_plan) {
          if (!d.invoke) continue;
          const key = d.key;
          const target = targets[key];
          const duration = streamDuration(target.length);

          // Mark running
          setAgents((prev) => ({ ...prev, [key]: { ...prev[key], phase: "running" } }));

          const chars = Math.max(target.length, 1);
          const intervalMs = Math.max(8, Math.floor(duration / chars));
          let i = 0;
          const id = setInterval(() => {
            i += Math.max(1, Math.floor(chars / 200));
            if (i >= chars) {
              clearInterval(id);
              setAgents((prev) => ({
                ...prev,
                [key]: { ...prev[key], phase: "done", output: target, progress: 100 },
              }));
            } else {
              const slice = target.slice(0, i);
              const prog = Math.min(100, Math.floor((i / chars) * 100));
              setAgents((prev) => ({
                ...prev,
                [key]: { ...prev[key], output: slice, progress: prog },
              }));
            }
          }, intervalMs);
          intervals.push(id);
        }
      }, stepDelay + 480),
    );

    return () => {
      for (const t of timers) clearTimeout(t);
      for (const i of intervals) clearInterval(i);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Are all dispatched agents done / all skipped agents skipped?
  const allDone = scenario.dispatch_plan.every(
    (d) =>
      (d.invoke && agents[d.key].phase === "done") ||
      (!d.invoke && agents[d.key].phase === "skipped"),
  );

  // Coordinator → synthesizing → done. Uses ref to guarantee one-shot
  // transition (avoids the useEffect cleanup race that previously killed the
  // 'done' timer when deps changed mid-transition).
  useEffect(() => {
    if (allDone && !synthStartedRef.current) {
      synthStartedRef.current = true;
      setCoordPhase("synthesizing");
      const t = setTimeout(() => setCoordPhase("done"), 900);
      // intentionally no cleanup — this is a one-shot transition
      void t;
    }
  }, [allDone]);

  const dispatched = scenario.dispatch_plan.filter((d) => d.invoke).length;
  const skipped = scenario.dispatch_plan.length - dispatched;

  return (
    <div className="flight">
      <div className="flight-header">
        <h1>Triaging incident</h1>
        <p className="muted">
          Coordinator inspects the incident, decides which sub-agents to invoke,
          dispatches them via service bindings, and hands the bundled findings to
          the synthesizer.
        </p>
      </div>

      <div className="coord-card">
        <div className="coord-card-head">
          <div className="coord-card-head-left">
            <span className="coord-icon" aria-hidden="true">✦</span>
            <div>
              <div className="coord-card-title">Triage Coordinator</div>
              <div className="coord-card-subtitle">
                {coordPhase === "analyzing"   && "Reading the incoming signal"}
                {coordPhase === "dispatching" && "Deciding which sub-agents to run"}
                {coordPhase === "running"     && `Running ${dispatched} sub-agent${dispatched === 1 ? "" : "s"} · ${skipped} skipped`}
                {coordPhase === "synthesizing" && "Synthesizing the triage card"}
                {coordPhase === "done"        && "Triage complete"}
              </div>
            </div>
          </div>
          <span className={`coord-phase-pill phase-${coordPhase}`}>
            {coordPhase === "done" ? "DONE" : "WORKING"}
          </span>
        </div>

        <div className="coord-card-body">
          <ol className="coord-steps">
            {coordSteps.map((step, idx) => (
              <li key={idx} className={`coord-step coord-step-${step.kind}`}>
                <CoordStepRow step={step} />
              </li>
            ))}
            {coordPhase === "analyzing" && coordSteps.length === 0 && (
              <li className="coord-step coord-step-loading">
                <span className="cursor">▍</span>
                <span className="muted">Reading the signal…</span>
              </li>
            )}
          </ol>
        </div>
      </div>

      <div className="sub-agent-grid">
        {(["command", "identity", "network", "activity"] as SubAgentKey[]).map((key) => {
          const state = agents[key];
          const dispatchInfo = dispatchMap.get(key);
          const meta = AGENT_META[key];
          const inputs = inputPreview(scenario, key);
          const verdict = verdictLine(scenario, key);
          return (
            <SubAgentCard
              key={key}
              meta={meta}
              state={state}
              inputs={inputs}
              verdict={verdict}
              invoked={dispatchInfo?.invoke ?? false}
              reason={dispatchInfo?.reason ?? ""}
            />
          );
        })}
      </div>

      <div className="flight-actions">
        <button
          type="button"
          className="button primary lg"
          onClick={onComplete}
          disabled={coordPhase !== "done"}
        >
          {coordPhase === "done" ? "View triage card →" : "Coordinator working…"}
        </button>
      </div>
    </div>
  );
}

// --- Baseball-card sub-agent panel ------------------------------------------

function SubAgentCard({
  meta,
  state,
  inputs,
  verdict,
  invoked,
  reason,
}: {
  meta: { name: string; tagline: string; focus: string; glyph: string; accent: string };
  state: AgentState;
  inputs: { label: string; value: string }[];
  verdict: string;
  invoked: boolean;
  reason: string;
}) {
  const phaseClass = state.phase;
  const statusBadge =
    state.phase === "done"     ? "DONE"
    : state.phase === "running" ? "RUNNING"
    : state.phase === "skipped" ? "SKIPPED"
    : invoked                   ? "QUEUED"
    :                             "PENDING";

  return (
    <div className={`bc bc-${phaseClass} ${state.phase === "skipped" ? "bc-skipped" : ""}`}>
      {/* Top accent strip */}
      <div className="bc-accent" style={{ background: meta.accent }} />

      {/* Header */}
      <div className="bc-head">
        <div
          className="bc-glyph"
          style={{
            background: state.phase === "skipped" ? "transparent" : meta.accent,
            color: state.phase === "skipped" ? meta.accent : "#fff",
            border: state.phase === "skipped" ? `1.5px dashed ${meta.accent}` : "none",
          }}
        >
          {meta.glyph}
        </div>
        <div className="bc-head-text">
          <div className="bc-name">{meta.name}</div>
          <div className="bc-tagline">{meta.tagline}</div>
        </div>
        <span className={`pill pill-${phaseClass}`}>{statusBadge}</span>
      </div>

      {/* Progress bar */}
      <div className="bc-progress">
        <div
          className="bc-progress-fill"
          style={{
            width: `${state.progress}%`,
            background: state.phase === "done" ? "var(--risk-low)" : meta.accent,
          }}
        />
      </div>

      {/* Input stats — what the agent is looking at */}
      <div className="bc-stats">
        <div className="bc-stats-label">FOCUS · {meta.focus}</div>
        {state.phase === "skipped" ? (
          <div className="bc-skipped-body">
            <div className="bc-skipped-title">Skipped by coordinator</div>
            <div className="muted">{reason}</div>
          </div>
        ) : (
          <dl className="bc-stat-list">
            {inputs.map((s) => (
              <div key={s.label} className="bc-stat-row">
                <dt>{s.label}</dt>
                <dd className="mono">{truncate(s.value, 38)}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>

      {/* Streaming output / result */}
      {state.phase !== "skipped" && (
        <div className="bc-output">
          {state.output}
          {state.phase === "running" && <span className="cursor">▍</span>}
        </div>
      )}

      {/* Footer: verdict when done */}
      {state.phase === "done" && (
        <div className="bc-footer">
          <span className="bc-verdict-label">VERDICT</span>
          <span className="bc-verdict-value">{verdict}</span>
        </div>
      )}
    </div>
  );
}

function truncate(s: string, n: number): string {
  return s.length <= n ? s : s.slice(0, n) + "…";
}

// --- Coordinator step row ----------------------------------------------------

function CoordStepRow({ step }: { step: CoordStep }) {
  switch (step.kind) {
    case "incoming":
      return (
        <div className="cstep cstep-incoming">
          <span className="cstep-label">INCOMING</span>
          <span className="cstep-id mono">{step.incidentId}</span>
          <span className="cstep-arrow">from</span>
          <span className="cstep-source">{step.source}</span>
        </div>
      );
    case "extracted":
      return (
        <div className="cstep cstep-extracted">
          <span className="cstep-label">EXTRACTED</span>
          <span className="cstep-chips">
            {step.fields.map((f) => (
              <span key={f} className="cstep-chip mono">{f}</span>
            ))}
          </span>
        </div>
      );
    case "dispatch": {
      const meta = AGENT_META[step.agent];
      return (
        <div className="cstep cstep-dispatch">
          <span
            className="cstep-mark cstep-mark-go"
            style={{ background: meta.accent }}
            aria-label="dispatch"
          >
            →
          </span>
          <div className="cstep-body">
            <div className="cstep-head">
              <span className="cstep-verb">Dispatch</span>
              <span className="cstep-agent">{meta.name}</span>
            </div>
            <div className="cstep-reason">{step.reason}</div>
          </div>
        </div>
      );
    }
    case "skip": {
      const meta = AGENT_META[step.agent];
      return (
        <div className="cstep cstep-skip">
          <span className="cstep-mark cstep-mark-skip" aria-label="skip">
            ×
          </span>
          <div className="cstep-body">
            <div className="cstep-head">
              <span className="cstep-verb cstep-verb-skip">Skip</span>
              <span className="cstep-agent cstep-agent-skip">{meta.name}</span>
            </div>
            <div className="cstep-reason cstep-reason-skip">{step.reason}</div>
          </div>
        </div>
      );
    }
  }
}
